import {
  canTaskBeCompleted,
  canTaskBeReviewed,
  canTaskBeSkipped,
  canTaskBeSnoozed,
  canTaskBeStarted,
  evaluateClientQuota,
  getCommentProps,
  getInitialComment,
  getTaskChatMessageAuthorInfo,
  groupWhispers,
  shouldPromptTaskReview,
} from '@frontend/api-client';
import type { TaskComment } from '@frontend/api-types';
import { P, match } from '@frontend/duck-tape';
import type { CreateTaskCommentFormSchema } from '@frontend/forms';
import { useCreateTaskCommentForm } from '@frontend/forms';
import { useEffect, useFeatureIsOn, useState, useTimedAnimation, useVisibilityController } from '@frontend/react';
import type { FlatListProps } from '@frontend/web-react';
import {
  Banner,
  Divider,
  DragAndDropModal,
  FlatList,
  Header,
  LoadingSpinnerPanel,
  PageContainer,
  Tag,
  YStack,
  useScrollView,
  useViewportSize,
} from '@frontend/web-react';
import { WebTypedFormData, safeCss } from '@frontend/web-utils';
import {
  ChatFooter,
  CompleteTaskModal,
  ReviewTaskModal,
  SnoozeTaskModal,
  TaskChatInitialActions,
  TaskChatMessageBubble,
  TaskChatResearchThreadWhisper,
  TaskChatSecureNoteCard,
  TaskChatTime,
  TaskChatWhisper,
  UnsnoozeTaskModal,
  WhisperGroup,
} from '@frontend/web/components';
import { TaskChatOptionSheetCard } from '@frontend/web/components/DomainSpecific/Tasks/TaskChatOptionSheetCard';
import { TaskDetailModal } from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailModal';
import { ResumeDraftTasksModal } from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailModal/ResumeDraftTasksModal';
import {
  useAnalytics,
  useCreateCommentMutation,
  useListCommentsQuery,
  useListDraftTasksQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveTaskQuery,
  useSkipTaskMutation,
  useStartTaskMutation,
  useUpdateTaskMutation,
} from '@frontend/web/hooks';
import { disconnectSocket, getSocket, handleMutation, initializeSocket } from '@frontend/web/utils';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import Confetti from 'react-confetti';

const Task = () => {
  const { enabled: isChannelWhispersEnabled } = useFeatureIsOn('is_channel_whispers_enabled');
  const navigate = useNavigate();
  const { taskId } = Route.useParams();
  const { isAnimationOn, turnOnAnimation } = useTimedAnimation({ duration: 5000 });
  const { height, width } = useViewportSize();
  const { track } = useAnalytics();
  const { data: client } = useRetrieveClientQuery();
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const { data: task, refetch: refetchTask } = useRetrieveTaskQuery(taskId);
  const { data: drafts } = useListDraftTasksQuery();
  const { scrollRef, scrollToBottom } = useScrollView({ onRenderScroll: 'bottom' });
  const [isSnoozeTaskModalOpen, { close: closeSnoozeTaskModal, open: openSnoozeTaskModal }] = useVisibilityController();
  const [isReviewTaskModalOpen, { close: closeReviewTaskModal, open: openReviewTaskModal }] = useVisibilityController();
  const [isUnsnoozeTaskModalOpen, { close: closeUnsnoozeTaskModal, open: openUnsnoozeTaskModal }] =
    useVisibilityController();
  const [isCompleteTaskModalOpen, { close: closeCompleteTaskModalOpen, open: openCompleteTaskModal }] =
    useVisibilityController();
  const [isTaskDetailModalOpen, { close: closeTaskDetailModal, open: openTaskDetailModal }] = useVisibilityController();
  const [isResumeDraftTasksModalOpen, { close: closeResumeDraftTasksModal, open: openResumeDraftTasksModal }] =
    useVisibilityController();
  const [createComment, { isLoading: isCreateCommentLoading }] = useCreateCommentMutation();
  const router = useRouter();
  // If user completes the task here, it's required. If they opt-in to review by pressing the banner, it's optional
  const [shouldForceReview, setShouldForceReview] = useState(false);
  const { data: comments = [], isLoading: isLoadingComments, isSuccess, refetch } = useListCommentsQuery(taskId);
  const [skipTask, { isLoading: isSkipTaskLoading }] = useSkipTaskMutation();
  const [startTask, { isLoading: isStartTaskLoading }] = useStartTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const isChatLoading = isSkipTaskLoading || isStartTaskLoading;

  const isDetailTagVisible =
    client?.household || task?.sheets?.length || task?.attachments?.length || task?.secureNotes.length;

  const { handleSubmit, setFieldValue, values } = useCreateTaskCommentForm({
    onSubmit: async (values, { resetForm }) =>
      handleMutation({
        mutation: () => {
          const typedForm = new WebTypedFormData<CreateTaskCommentFormSchema>();
          typedForm.append('body', values.body || 'See attached');
          typedForm.appendLocalFiles('attachments', values.attachments);
          return createComment({ formData: typedForm, taskId }).unwrap();
        },
        onSuccess: () => {
          resetForm();
          scrollToBottom();
        },
        showErrorToast: true,
        showSuccessToast: false,
      }),
  });

  const [redirectToTasksListAfterReview, setRedirectToTasksListAfterReview] = useState(true);

  const { isUnread, searchThread } = task ?? {};
  const canBeSnoozed = task && canTaskBeSnoozed(task);
  const canBeUnsnoozed = task?.status === 'snoozed';
  const canBeCompleted = task && canTaskBeCompleted(task);

  const onClickLeaveReview =
    ({ redirectToTasksList }: { redirectToTasksList: boolean }) =>
    () => {
      // If user is opting in to leave a review (not forced csat), don't redirect them to home
      setRedirectToTasksListAfterReview(redirectToTasksList);
      openReviewTaskModal();
      track({ data: { taskId }, event: 'reviewTasksFormViewed', topic: 'tasks' });
    };

  const handleCloseReview = () => {
    closeReviewTaskModal();
  };

  const handleCompleteReview = () => {
    if (clientStatus && !evaluateClientQuota(clientStatus).isThrottled && drafts?.length) {
      openResumeDraftTasksModal();
    } else if (redirectToTasksListAfterReview) {
      navigate({ search: { tab: 'waiting_on_you' }, to: '/app/task' });
    }
  };

  const setupSocket = ({ taskId }: { taskId: string }) => {
    if (getSocket('task')) {
      disconnectSocket();
    }

    const socket = initializeSocket({
      channelType: 'task',
      params: { task_id: taskId },
    });
    return socket;
  };

  useEffect(() => {
    const socket = setupSocket({ taskId });

    socket.on('receive_message', () => {
      refetch();
      // Since task info like secure notes, option sheets, attachments
      // are now displayed in task detail page,
      // task detail need to be refetched to get the latest data
      refetchTask();
    });

    return () => {
      socket.off('receive_message');
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isSuccess && isUnread) {
      updateTask({ data: { isUnread: false }, id: taskId });
    }
  }, [isSuccess]);

  useEffect(() => {
    scrollToBottom();
  }, [comments, scrollToBottom]);

  const handleClickSkipTask = () => {
    track({ data: { taskId }, event: 'startTaskButtonSelected', topic: 'tasks' });
    handleMutation({
      mutation: async () => skipTask(taskId).unwrap(),
      showErrorToast: true,
      showSuccessToast: true,
      successMessage: 'You have skipped the task this time.',
    });
  };

  const handleClickStartTask = () => {
    track({ data: { taskId }, event: 'skipTaskButtonSelected', topic: 'tasks' });
    handleMutation({
      mutation: async () => startTask(taskId).unwrap(),
      showErrorToast: true,
      showSuccessToast: true,
      successMessage: 'Task started!',
    });
  };

  const commentsWithDescription = task
    ? isChannelWhispersEnabled
      ? groupWhispers([
          getInitialComment(task, client, {
            labelClassName: safeCss('text-caption text-textPrimary font-semibold'),
            textClassName: safeCss('text-p-md text-textPrimary'),
          }),
          ...comments,
        ])
      : [
          getInitialComment(task, client, {
            labelClassName: safeCss('text-caption text-textPrimary font-semibold'),
            textClassName: safeCss('text-p-md text-textPrimary'),
          }),
          ...comments,
        ]
    : [];

  const renderItem: FlatListProps<TaskComment | TaskComment[]>['renderItem'] = ({ index, item }) => {
    if (Array.isArray(item)) {
      // Render WhisperGroup for grouped whispers
      return (
        <YStack gapY="sm">
          {item[0]?.createdAt ? <TaskChatTime time={item[0].createdAt} /> : null}
          <WhisperGroup comments={item} taskId={taskId} />
        </YStack>
      );
    }
    const { attachments, author, body, createdAt, secureNote, sheets } = item;
    const { isAuthorVisible, isDividerVisible, isTimeVisible } = getCommentProps(commentsWithDescription, index);
    const isFirstMessage = isTimeVisible && index === commentsWithDescription.length - 1;

    const content = (() => {
      if (secureNote) {
        const { id, isActive, title } = secureNote;
        return (
          <TaskChatSecureNoteCard
            className="ml-xl"
            isActive={isActive}
            secureNoteId={id}
            taskId={taskId}
            title={title}
          />
        );
      } else if (sheets?.[0]) {
        const { id, imageUrl, title } = sheets[0];
        return <TaskChatOptionSheetCard className="ml-xl" imageUrl={imageUrl} optionSheetId={id} title={title} />;
      }

      return match(author)
        .with({ type: 'system_bot' }, () => <TaskChatWhisper body={body} createdAt={createdAt} />)
        .with({ type: P.union('agent', 'ai_bot', 'client') }, (author) => (
          <TaskChatMessageBubble
            {...item}
            attachments={attachments}
            authorInfo={getTaskChatMessageAuthorInfo(author)}
            body={body}
            isAuthorVisible={isAuthorVisible}
          />
        ))
        .exhaustive();
    })();

    return (
      <YStack gapY="md">
        {isTimeVisible ? <TaskChatTime time={createdAt} /> : null}
        {/*
           For now, don't allow clicking to view the thread unless user is the owner of the task
           We don't yet support sharing threads across household members (TECH-3543)
        */}
        {isFirstMessage && searchThread ? (
          <TaskChatResearchThreadWhisper canViewThread={task?.ownerId === client?.id} searchThread={searchThread} />
        ) : null}
        {content}
        {isDividerVisible && !isChannelWhispersEnabled ? <Divider /> : null}
      </YStack>
    );
  };

  const onClickLeaveReviewBanner = () => {
    track({ data: { taskId }, event: 'reviewTaskBannerSelected', topic: 'tasks' });
    onClickLeaveReview({ redirectToTasksList: false })();
  };

  return (
    <PageContainer className="bg-trueWhite" isScrollable={false} padding="none">
      {task ? (
        <FlatList
          ListFooterComponent={
            <YStack className="bottom-none bg-trueWhite sticky">
              {canTaskBeSkipped(task) || canTaskBeStarted(task) ? (
                <TaskChatInitialActions
                  disabled={isChatLoading}
                  isSkipTaskLoading={isSkipTaskLoading}
                  isStartTaskLoading={isStartTaskLoading}
                  onSkip={handleClickSkipTask}
                  onStart={handleClickStartTask}
                  task={task}
                />
              ) : null}
              {canTaskBeReviewed(task) ? (
                <Banner
                  className="mb-md w-full max-w-screen-md self-center"
                  color="white"
                  leftIconName="IconStar"
                  onClick={onClickLeaveReviewBanner}
                  rightIconName="IconArrowRight"
                  title="Leave a review to say thanks"
                />
              ) : null}
              {task && task?.status !== 'archived' ? (
                <ChatFooter
                  className="pb-md w-full max-w-screen-md self-center"
                  filesValue={values.attachments}
                  isSubmitLoading={isCreateCommentLoading}
                  onChangeFiles={(attachments) => setFieldValue('attachments', attachments)}
                  onChangeText={(body) => setFieldValue('body', body)}
                  onClickSubmit={handleSubmit}
                  onComplete={canBeCompleted ? openCompleteTaskModal : undefined}
                  onSnooze={canBeSnoozed ? openSnoozeTaskModal : undefined}
                  onUnsnooze={canBeUnsnoozed ? openUnsnoozeTaskModal : undefined}
                  submitDisabled={!values.body && !values.attachments.length}
                  submitShortcut="Enter"
                  textInputDisabled={isCreateCommentLoading}
                  textValue={values.body}
                />
              ) : null}
            </YStack>
          }
          ListHeaderComponent={
            <Header
              className="top-none bg-trueWhite sticky"
              onClickBack={router.history.back}
              rightContent={
                isDetailTagVisible ? <Tag label="Details" onClick={() => openTaskDetailModal()} /> : undefined
              }
              title={task?.name ?? ''}
            />
          }
          ListLoadingComponent={<LoadingSpinnerPanel fullScreen />}
          className="px-md pb-md flex-1"
          contentContainerClassName={safeCss('py-md gap-y-md max-w-screen-md self-center w-full justify-end flex-1')}
          // TODO (ethan): Reverse immutably instead
          // eslint-disable-next-line fp/no-mutating-methods
          data={commentsWithDescription.reverse()}
          inverted
          isLoading={isLoadingComments}
          ref={scrollRef}
          renderItem={renderItem}
        />
      ) : (
        <LoadingSpinnerPanel fullScreen />
      )}

      {task ? (
        <CompleteTaskModal
          isOpen={isCompleteTaskModalOpen}
          onClose={closeCompleteTaskModalOpen}
          onComplete={() => {
            refetchTask();
            turnOnAnimation();
            // Little hack to force the review modal to open since the task was just completed
            if (shouldPromptTaskReview({ ...task, status: 'solved' })) {
              setShouldForceReview(true);
              openReviewTaskModal();
            }
          }}
          openModal={openCompleteTaskModal}
          task={task}
          taskId={taskId}
        />
      ) : null}
      <ReviewTaskModal
        isOpen={isReviewTaskModalOpen}
        onClose={handleCloseReview}
        onComplete={handleCompleteReview}
        openModal={onClickLeaveReview({ redirectToTasksList: true })}
        overrideAllowSkip={!shouldForceReview}
        taskId={taskId}
      />
      {task ? (
        <SnoozeTaskModal
          isOpen={isSnoozeTaskModalOpen}
          onClose={closeSnoozeTaskModal}
          onComplete={refetchTask}
          openModal={openSnoozeTaskModal}
          task={task}
          taskId={taskId}
        />
      ) : null}
      {task ? (
        <UnsnoozeTaskModal
          isOpen={isUnsnoozeTaskModalOpen}
          onClose={closeUnsnoozeTaskModal}
          onComplete={refetchTask}
          openModal={openUnsnoozeTaskModal}
          taskId={taskId}
        />
      ) : null}
      {isAnimationOn ? <Confetti height={height} width={width} /> : null}
      <DragAndDropModal
        fileLimitReached={values.attachments.length >= 5}
        onFilesDropped={(attachments) => setFieldValue('attachments', [...values.attachments, ...attachments])}
      />
      <TaskDetailModal isOpen={isTaskDetailModalOpen} onClose={closeTaskDetailModal} task={task} />
      <ResumeDraftTasksModal isOpen={isResumeDraftTasksModalOpen} onClose={closeResumeDraftTasksModal} />
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/task/$taskId')({
  component: Task,
});
